import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Routes } from "@ctypes";
import { usePartnerBank } from "@hooks";
import { i18n, Translations } from "@i18n";
import { Analytics } from "@services";
import { AnalyticsEvent } from "@ctypes/analytics";
import { home } from "./tokens";

const Banner = styled.div<{ $background: string }>`
  position: relative;
  overflow: hidden;
  border-radius: ${home.radius};
  background: ${({ $background }) => $background};
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const Watermark = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  svg,
  img {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const Lockup = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const Copy = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
`;

const Cta = styled.button`
  position: relative;
  flex-shrink: 0;
  border: 0;
  cursor: pointer;
  border-radius: 999px;
  background: #ffffff;
  color: #111111;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
`;

export const PartnerBanner = () => {
  const navigate = useNavigate();
  const { partnerBank } = usePartnerBank();

  if (!partnerBank) return null;

  const { colors, logos, accountName, displayName } = partnerBank;
  const { Light: PartnerLogo, BannerWatermark } = logos;

  const handlePress = () => {
    Analytics.track(AnalyticsEvent.PartnerBannerPressed, { partner: displayName });
    navigate(Routes.Partnership);
  };

  return (
    <Banner $background={colors.banner}>
      {BannerWatermark && (
        <Watermark aria-hidden="true">
          <BannerWatermark />
        </Watermark>
      )}
      <Lockup>
        <PartnerLogo height={logos.bannerHeight} />
      </Lockup>
      <Copy>
        <Title>{i18n.t(Translations.PartnerBannerTitle, { bank: accountName })}</Title>
        <Subtitle>{i18n.t(Translations.PartnerBannerSubtitle)}</Subtitle>
      </Copy>
      <Cta type="button" onClick={handlePress}>
        {i18n.t(Translations.PartnerBannerCTA)}
      </Cta>
    </Banner>
  );
};
